// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import exportToProductList from "@werner/App/Handlers/ProductExport/ExportToProductList";
import { ExportToProductListApiParameter } from "@werner/App/Services/ExportoProductListService";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
// XNG-Changes: import twobuttonmodal from override
import Modal, { ModalPresentationProps } from "@werner/MobiusOverrides/Modal/Modal";
import Exit from "@werner/XDL/Icons/Exit";
import { createBreadcrumbActionLink, mergeWithCss, modalButtonsActionStyles } from "@werner/XDL/Utilities";
import { xValidateOnSubmit } from "@werner/XDL/Utilities/xValidate/xFieldValidator";
import React, { ChangeEvent, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    type: ExportToProductListApiParameter["type"];
    id?: string;
    text?: string;
    extendedStyles?: ExportToProductListLinkStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProduct;

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    exportToProductList,
};

export interface ExportToProductListLinkStyles {
    link?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    emailLabel?: TypographyProps;
    yourEmailTextField?: TextFieldProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    shareButton?: ButtonPresentationProps;
}

export const exportToProductListLinkStyles: ExportToProductListLinkStyles = {
    link: {
        ...createBreadcrumbActionLink(Exit),
    },
    emailLabel: {
        variant: "p",
        css: css`
            margin-bottom: 12px;
        `,
    },
    cancelButton: {
        ...modalButtonsActionStyles.cancelButton,
    },
    shareButton: {
        ...modalButtonsActionStyles.submitButton,
    },
};

const formId = "exportToProductListForm";

const ExportToProductListLink = ({ text, id, type, session, exportToProductList, extendedStyles }: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeWithCss(exportToProductListLinkStyles, extendedStyles));
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const initialYourEmail = isAuthenticated ? session.email || "" : "";
    const [yourEmail, setYourEmail] = useState(initialYourEmail);
    const [yourEmailError, setYourEmailError] = useState("");
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const shareLinkClickHandler = () => {
        resetFields();
        setModalIsOpen(true);
    };

    const requiredFieldMessage = translate("This is a required field");
    const emailFieldMessage = translate("Enter a valid email address");
    const checkForErrors = () => {
        const regexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");

        const localYourEmailError = !yourEmail ? requiredFieldMessage : regexp.test(yourEmail) ? "" : emailFieldMessage;
        setYourEmailError(localYourEmailError);

        return !!localYourEmailError;
    };

    const resetFields = () => {
        setYourEmail(initialYourEmail);
        setYourEmailError("");
    };

    const yourEmailChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setYourEmail(event.target.value);
        setYourEmailError(!event.target.value ? requiredFieldMessage : "");
    };

    const shareButtonClickHandler = () => {
        const errors = xValidateOnSubmit(formId);
        if (errors.length || checkForErrors()) {
            return;
        }
        exportToProductList({
            id,
            type,
            email: yourEmail,
            onSuccess: () => {
                toasterContext.addToast({
                    body: siteMessage("Custom_ExportProductListSuccess"),
                    messageType: "success",
                });
                modalCloseHandler();
            },
            onError: () => {
                toasterContext.addToast({
                    body: siteMessage("Custom_ExportProductListError"),
                    messageType: "danger",
                });
            },
        });
        toasterContext.addToast({
            body: siteMessage("Custom_ExportProductListInProgress"),
            messageType: "info",
        });
        modalCloseHandler();
    };

    if (!type || !isAuthenticated) {
        return null;
    }

    // XNG-Changes
    const exportToProductListLinktAction = (
        <>
            <Button {...styles.cancelButton} onClick={modalCloseHandler} data-test-selector="exportToProductListCancel">
                {translate("Cancel")}
            </Button>
            <Button
                {...styles.shareButton}
                onClick={shareButtonClickHandler}
                data-test-selector="exportToProductListSubmit"
            >
                {translate("Ok")}
            </Button>
        </>
    );
    // XNG-Changes

    return (
        <>
            <Link
                {...styles.link}
                onClick={shareLinkClickHandler}
                data-test-selector="exportToProductListLink"
                aria-label={`${text} || Export Product List `}
            >
                {text || translate("Export Product List")}
            </Link>
            {/* //XNG-Changes */}
            <Modal
                {...styles.modal}
                headline={translate("Export Product List Confirmation")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
                actions={exportToProductListLinktAction}
            >
                <Typography {...styles.emailLabel}>
                    {translate("The product list will be exported and emailed to")}
                </Typography>
                <TextField
                    label={translate("Your Email")}
                    value={yourEmail}
                    error={yourEmailError}
                    onChange={yourEmailChangeHandler}
                    data-test-selector="exportToProductListYourEmail"
                    required={true}
                    type="email"
                    {...{ formId }}
                    {...styles.yourEmailTextField}
                />
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProduct(ExportToProductListLink));
